import { useEffect, useRef, useState } from "react";
import { Sparkles, Calendar, Paperclip, X, FileDown } from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { toast } from "sonner";

const BASE_GRADES = [
    "1ro Primaria", "2do Primaria", "3ro Primaria", "4to Primaria", "5to Primaria", "6to Primaria",
    "1ro Secundaria", "2do Secundaria", "3ro Secundaria", "4to Secundaria", "5to Secundaria",
];

const GRADE_SECTIONS = BASE_GRADES.flatMap((g) =>
    (g === "5to Secundaria" ? ["A", "B", "C"] : ["A", "B"]).map((s) => `${g} ${s}`)
);

export default function Horarios() {
    const [description, setDescription] = useState(
        "Necesito un horario para 5to Primaria A con materias: Matemáticas, Comunicación, Ciencias, Historia, Inglés y Educación Física. Profesores disponibles: Juan (Matemáticas), María (Comunicación), Carlos (Ciencias), Ana (Historia), Lucía (Inglés), Pedro (Ed. Física). Horario: 7:30 AM a 2:30 PM con 1 hora de almuerzo.",
    );
    const [grade, setGrade] = useState(GRADE_SECTIONS[4]);
    const [generating, setGenerating] = useState(false);
    const [schedules, setSchedules] = useState([]);
    const [attachment, setAttachment] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    async function load() {
        const r = await api.get("/ai/schedules");
        setSchedules(r.data || []);
    }
    useEffect(() => { load(); }, []);

    async function generate() {
        setGenerating(true);
        try {
            await api.post("/ai/schedule", {
                description,
                grade,
                file_id: attachment?.id || null,
            });
            toast.success("Horario generado");
            setAttachment(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error");
        } finally {
            setGenerating(false);
        }
    }

    async function onPickFile(ev) {
        const file = ev.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/files/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setAttachment(data);
            toast.success("Plantilla cargada");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error subiendo archivo");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    }

    return (
        <div className="p-6 md:p-10 fade-in" data-testid="horarios-page">
            <h1 className="text-3xl font-semibold mb-1">Horarios</h1>
            <p className="text-edu-body mb-6">Generador de horarios escolares con IA. Adjunta una plantilla PDF/imagen como referencia.</p>

            <div className="edu-card p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4 inline-flex items-center gap-2">
                    <Sparkles size={18} className="text-edu-gold" /> Generar con IA
                </h3>
                <div className="space-y-3">
                    <select
                        value={grade}
                        onChange={(e) => setGrade(e.target.value)}
                        className="edu-input"
                        data-testid="schedule-grade"
                    >
                        {GRADE_SECTIONS.map((g) => (
                            <option key={g}>{g}</option>
                        ))}
                    </select>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={5}
                        className="edu-input w-full"
                        placeholder="Describe materias, profesores y bloques de horario…"
                        data-testid="schedule-description"
                    />
                    <div>
                        <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-2 block">
                            Adjunto (opcional, PDF o imagen)
                        </span>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,image/*"
                            onChange={onPickFile}
                            className="hidden"
                            data-testid="schedule-file-input"
                        />
                        {attachment ? (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-edu-soft border border-edu max-w-sm">
                                <Paperclip size={14} className="text-edu-navy" />
                                <span className="text-sm flex-1 truncate" title={attachment.original_filename}>
                                    {attachment.original_filename}
                                </span>
                                <button
                                    onClick={() => setAttachment(null)}
                                    className="text-edu-muted hover:text-red-700"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl border border-edu hover:bg-gray-50 disabled:opacity-50"
                            >
                                <Paperclip size={14} /> {uploading ? "Subiendo…" : "Adjuntar PDF o imagen"}
                            </button>
                        )}
                    </div>
                    <button
                        onClick={generate}
                        disabled={generating}
                        className="edu-btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-50"
                        data-testid="generate-schedule"
                    >
                        <Sparkles size={14} /> {generating ? "Generando…" : "Generar Horario"}
                    </button>
                </div>
            </div>

            <h3 className="text-lg font-semibold mb-3">Horarios generados</h3>
            <div className="space-y-4">
                {schedules.length === 0 && <p className="text-sm text-edu-muted">Aún no hay horarios.</p>}
                {schedules.map((s) => (
                    <div key={s.id} className="edu-card p-6" data-testid={`schedule-${s.id}`}>
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <Calendar size={18} className="text-edu-navy" />
                                <h4 className="font-semibold">{s.grade}</h4>
                            </div>
                            <span className="text-xs text-edu-muted">{(s.created_at || "").slice(0, 10)}</span>
                        </div>
                        <pre className="text-sm whitespace-pre-wrap font-sans text-edu-body">{s.content}</pre>
                        {s.file_id && (
                            
                                <a 
                                href={fileUrl(s.file_id)}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-3 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                            >
                                <FileDown size={12} /> Descargar adjunto
                            </a>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
